import { Lock, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface FieldMapping {
  id: string;
  hubspotProperty: string;
  localColumn: string;
  columnType: string;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MappableColumn {
  name: string;
  type: string;
}

interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  category: string;
}

interface DealMappingsSectionProps {
  initialMappings: FieldMapping[];
  mappableColumns: MappableColumn[];
  hubspotProperties: HubSpotProperty[];
}

export function DealMappingsSection({
  initialMappings,
  mappableColumns,
  hubspotProperties,
}: DealMappingsSectionProps) {
  const [mappings, setMappings] = useState<FieldMapping[]>(initialMappings);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<FieldMapping | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FieldMapping | null>(null);

  // Form state
  const [hubspotProperty, setHubspotProperty] = useState("");
  const [localColumn, setLocalColumn] = useState("");
  const [saving, setSaving] = useState(false);
  const [propertySearch, setPropertySearch] = useState("");

  const mappedColumns = new Set(mappings.map((m) => m.localColumn));
  const mappedProperties = new Set(mappings.map((m) => m.hubspotProperty));

  const availableColumns = mappableColumns.filter(
    (col) => !mappedColumns.has(col.name) || col.name === editingMapping?.localColumn,
  );

  const availableProperties = hubspotProperties.filter(
    (p) => !mappedProperties.has(p.name) || p.name === editingMapping?.hubspotProperty,
  );

  const selectedColumnType = mappableColumns.find((c) => c.name === localColumn)?.type ?? "—";
  const selectedPropertyLabel =
    hubspotProperties.find((p) => p.name === hubspotProperty)?.label || hubspotProperty;

  // Build a lookup for the table display
  const propertyLabelMap = new Map(hubspotProperties.map((p) => [p.name, p.label]));

  const refreshMappings = useCallback(async () => {
    const res = await fetch("/api/deals/mappings");
    const data = await res.json();
    setMappings(data.mappings ?? []);
  }, []);

  function openAddDialog() {
    setEditingMapping(null);
    setHubspotProperty("");
    setLocalColumn("");
    setDialogOpen(true);
  }

  function openEditDialog(mapping: FieldMapping) {
    setEditingMapping(mapping);
    setHubspotProperty(mapping.hubspotProperty);
    setLocalColumn(mapping.localColumn);
    setDialogOpen(true);
  }

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!hubspotProperty.trim() || !localColumn) {
      toast.error("Both fields are required");
      return;
    }
    setSaving(true);
    try {
      const isEdit = editingMapping !== null;
      const url = isEdit ? `/api/deals/mappings/${editingMapping.id}` : "/api/deals/mappings";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hubspotProperty: hubspotProperty.trim(), localColumn }),
      });

      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || "Failed to save mapping");
        return;
      }

      await refreshMappings();
      setDialogOpen(false);
      toast.success(isEdit ? "Mapping updated" : "Mapping added");
    } catch {
      toast.error("Failed to save mapping");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/deals/mappings/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || "Failed to delete mapping");
        return;
      }
      await refreshMappings();
      setDeleteTarget(null);
      toast.success("Mapping deleted");
    } catch {
      toast.error("Failed to delete mapping");
    }
  }

  const filteredProperties = useMemo(() => {
    const q = propertySearch.toLowerCase();
    const filtered = q
      ? availableProperties.filter(
          (p) => p.name.toLowerCase().includes(q) || p.label.toLowerCase().includes(q),
        )
      : availableProperties;
    return {
      standard: filtered.filter((p) => p.category === "standard"),
      custom: filtered.filter((p) => p.category === "custom"),
    };
  }, [availableProperties, propertySearch]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm font-medium">Field Mappings</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {mappings.length} properties mapped from HubSpot to local database
            </p>
          </div>
          <Button size="sm" onClick={openAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Mapping
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {mappings.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No field mappings configured.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs uppercase tracking-wider">
                    HubSpot Property
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Local Column</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs w-fit">
                          {mapping.hubspotProperty}
                        </code>
                        {propertyLabelMap.get(mapping.hubspotProperty) && (
                          <span className="text-xs text-muted-foreground">
                            {propertyLabelMap.get(mapping.hubspotProperty)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {mapping.localColumn}
                        </code>
                        {mapping.isSystem && (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground/60" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={mapping.columnType === "real" ? "secondary" : "outline"}>
                        {mapping.columnType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => openEditDialog(mapping)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteTarget(mapping)}
                          disabled={mapping.isSystem}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMapping ? "Edit Mapping" : "Add Mapping"}</DialogTitle>
            <DialogDescription>
              Map a HubSpot deal property to a local database column.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* HubSpot Property — inline searchable list */}
            <div className="space-y-2">
              <Label>HubSpot Property</Label>
              {hubspotProperty && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {selectedPropertyLabel}
                    <span className="ml-1 text-muted-foreground">({hubspotProperty})</span>
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      setHubspotProperty("");
                      setPropertySearch("");
                    }}
                  >
                    Change
                  </Button>
                </div>
              )}
              {!hubspotProperty && (
                <div className="rounded-md border">
                  <div className="flex items-center gap-2 border-b px-3 py-2">
                    <Search className="h-4 w-4 shrink-0 opacity-50" />
                    <input
                      className="flex h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      placeholder="Search properties..."
                      value={propertySearch}
                      onChange={(e) => setPropertySearch(e.target.value)}
                    />
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {filteredProperties.standard.length === 0 &&
                    filteredProperties.custom.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No properties found.
                      </p>
                    ) : (
                      <>
                        {filteredProperties.standard.length > 0 && (
                          <div>
                            <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                              Standard
                            </div>
                            {filteredProperties.standard.map((prop) => (
                              <button
                                key={prop.name}
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                                onClick={() => {
                                  setHubspotProperty(prop.name);
                                  setPropertySearch("");
                                }}
                              >
                                <div className="flex flex-col">
                                  <span>{prop.label}</span>
                                  <span className="text-xs text-muted-foreground">{prop.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {filteredProperties.custom.length > 0 && (
                          <div>
                            <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                              Custom
                            </div>
                            {filteredProperties.custom.map((prop) => (
                              <button
                                key={prop.name}
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                                onClick={() => {
                                  setHubspotProperty(prop.name);
                                  setPropertySearch("");
                                }}
                              >
                                <div className="flex flex-col">
                                  <span>{prop.label}</span>
                                  <span className="text-xs text-muted-foreground">{prop.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Local Column — Select dropdown */}
            <div className="space-y-2">
              <Label>Local Column</Label>
              <Select value={localColumn} onValueChange={setLocalColumn}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a column" />
                </SelectTrigger>
                <SelectContent>
                  {availableColumns.map((col) => (
                    <SelectItem key={col.name} value={col.name}>
                      {col.name}
                      <span className="ml-2 text-muted-foreground">({col.type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Auto-derived column type */}
            {localColumn && (
              <div className="space-y-2">
                <Label>Column Type</Label>
                <p className="text-sm text-muted-foreground">{selectedColumnType}</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : editingMapping ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Mapping</DialogTitle>
            <DialogDescription>
              Remove the mapping for{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {deleteTarget?.hubspotProperty}
              </code>
              ? This property will no longer be synced from HubSpot.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
